import React, { createContext, useContext, FC, PropsWithChildren } from 'react';
import { usePermissions } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { useApi } from '~/hooks';
import { removeDraftPostfix } from '~/hooks/draft';
import { getApprovalCaseForObject } from '~/api/approvals';
import { useCheckApprovalCasesLicence } from '~/hooks/useCheckTenantLicense';
import { getObjectTypeByResource } from '~/utils/profileType';
import { matchRecordPage } from '~/utils/recordPages';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import { ObjectType } from '~/types/ObjectType';

export interface ApprovalCaseState {
  /** This kind of record can have approval cases — regardless of the tenant licence. */
  isAvailable: boolean;
  /** The change to this record that is awaiting approval, if any. */
  pendingCase: Partial<ApprovalCaseDetail> | undefined;
  isLoading: boolean;
  canViewCase: boolean;
  /** Call after submitting a change, which is what creates a case. */
  refetch: () => void;
}

const DEFAULT_STATE: ApprovalCaseState = {
  isAvailable: false,
  pendingCase: undefined,
  isLoading: false,
  canViewCase: false,
  refetch: () => {},
};

const ApprovalCaseContext = createContext<ApprovalCaseState>(DEFAULT_STATE);

/** The approval case blocking the record currently open. */
interface ProviderProps {
  /** The object to ask about, when it is not the one the url points at. */
  objectType?: ObjectType;
  objectId?: string;
}

const useApprovalCaseState = (override: ProviderProps): ApprovalCaseState => {
  const location = useLocation();
  const { permissions } = usePermissions();

  const page = matchRecordPage(location.pathname);
  const isOverridden = !!(override.objectType && override.objectId);

  const recordId = isOverridden
    ? override.objectId
    : page?.id
    ? removeDraftPostfix(page.id)
    : undefined;
  const objectType = isOverridden
    ? override.objectType
    : page?.resource
    ? getObjectTypeByResource(page.resource)
    : undefined;

  const isAvailable = isOverridden || !!page?.approvals;
  const { isFeatureEnabled, isLoading: isLoadingLicence } = useCheckApprovalCasesLicence();

  const canViewCase =
    !!permissions?.includes('approvalCase.view') || !!permissions?.includes('approvalCase.admin');

  const [{ data: cases, fetching }, refetch] = useApi<Partial<ApprovalCaseDetail>[]>(
    () =>
      isAvailable && isFeatureEnabled && objectType && recordId
        ? getApprovalCaseForObject(objectType, recordId)
        : Promise.resolve(),
    [isAvailable, isFeatureEnabled, objectType, recordId]
  );

  return {
    isAvailable,
    // The record exists, so the only case about it is one that changes it.
    pendingCase: cases?.find((c) => c?.actionType === 'UPDATE'),
    isLoading: isLoadingLicence || fetching,
    canViewCase,
    refetch,
  };
};

/**
 * Mounted at the top for the record the url points at. Mount another one around a
 * drawer showing a different object, naming that object.
 */
export const ApprovalCaseProvider: FC<PropsWithChildren<ProviderProps>> = ({
  children,
  ...override
}) => {
  const state = useApprovalCaseState(override);

  return <ApprovalCaseContext.Provider value={state}>{children}</ApprovalCaseContext.Provider>;
};

/** Approval state of the record currently open. */
export const useApprovalCase = (): ApprovalCaseState => useContext(ApprovalCaseContext);

export const useRecordLockForEdit = (): { isLocked: boolean; isLoading: boolean } => {
  const { isAvailable, isLoading, pendingCase } = useApprovalCase();

  return { isLocked: isAvailable && !!pendingCase, isLoading: isAvailable && isLoading };
};
